<template>
    <!-- Record rail beside a process/sheet/value engine/powerlist: Sign-offs, Files, Comments, History.
         Public users sign; admins see who has signed and the History (audit log). -->
    <aside :class="collapsed ? 'rec-rail rec-rail-collapsed' : 'rec-rail'" aria-label="Sign-offs, files, comments">
        <!-- Hidden: just a button to bring the rail back (remembered on this device, see setCollapsed). Only
             beside the content: once the rail stacks below it, it always shows and these buttons don't (style.scss) -->
        <button v-if="collapsed" type="button" class="rz-toggle rz-show" title="Show sign-offs, files and comments" aria-label="Show sign-offs, files and comments" @click="setCollapsed(false)">
            <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M15 4v16M10 10l-2 2 2 2" /></svg>
        </button>
        <div class="rz">
            <button type="button" class="rz-toggle rz-hide" title="Hide" aria-label="Hide sign-offs, files and comments" @click="setCollapsed(true)">
                <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M15 4v16M8 10l2 2-2 2" /></svg>
            </button>
            <section class="rz-hero">
                <template v-if="isAdmin">
                    <div class="rz-top">
                        <div :class="signedCount == required.length && required.length ? 'rz-ring ok' : 'rz-ring'" role="img" :aria-label="signedCount + ' of ' + required.length + ' signed'">
                            <svg viewBox="0 0 54 54"><circle cx="27" cy="27" r="22" class="trk" /><circle cx="27" cy="27" r="22" class="bar" :stroke-dasharray="ringDash" /></svg>
                            <span><b>{{ signedCount }}</b>/{{ required.length }}</span>
                        </div>
                        <div class="rz-ttl">
                            <h3>Sign-offs</h3>
                            <div class="rz-sub">on version {{ version }}</div>
                            <template v-if="groups.stale.length || groups.none.length">
                                <div v-if="groups.stale.length" class="rz-st warn"><i></i>{{ groups.stale.length }} need to sign again</div>
                                <div v-if="groups.none.length" class="rz-st"><i></i>{{ groups.none.length }} haven't signed</div>
                            </template>
                            <div v-else-if="required.length" class="rz-st ok"><i></i>Everyone has signed</div>
                        </div>
                    </div>
                    <div v-if="pending.length" class="rz-stack" aria-hidden="true">
                        <span v-for="p in pending.slice(0, 5)" :key="p.key" :class="p.latest ? 'rz-av stale' : 'rz-av'" :title="p.name">{{ initials(p.name) }}</span>
                        <span v-if="pending.length > 5" class="rz-av more">+{{ pending.length - 5 }}</span>
                    </div>
                    <div class="rz-why" style="text-align:left">Admins don't sign. To sign off, switch to your Standard User account using the toggle at the top.</div>
                    <button type="button" class="rz-link" :aria-expanded="open == 'sign'" @click="toggle('sign')">
                        {{ open == 'sign' ? 'Hide' : "See who's signed" }}
                        <svg viewBox="0 0 24 24" :class="open == 'sign' ? 'rz-chev up' : 'rz-chev'"><path d="M6 9l6 6 6-6" /></svg>
                    </button>
                    <div v-if="open == 'sign'" class="rz-signlist">
                        <div v-for="group in signGroups" :key="group.id" class="rp-group">
                            <div :class="'rp-gh ' + group.cls"><span>{{ group.title }} <b>{{ group.people.length }}</b></span></div>
                            <div class="rp-scroll">
                                <div v-for="p in (expanded[group.id] ? group.people : group.people.slice(0, 4))" :key="p.key" class="rp-person">
                                    <div class="rp-av">{{ initials(p.name) }}</div>
                                    <div class="rp-pmain">
                                        <div class="rp-pn">{{ p.name }}</div>
                                        <div v-if="p.latest" class="rp-ps">
                                            <span v-if="!isCurrent(p.latest)">Signed an earlier version · </span>{{ fmtDay(p.latest.Date) }}
                                        </div>
                                        <div v-for="(s, j) in p.cellSignOffs" :key="j" class="rp-ps">
                                            <a @click.prevent="showSignOffCells(s)">{{ s.SeatName }}</a> · {{ fmtDay(s.Date) }}
                                        </div>
                                    </div>
                                    <img v-if="p.latest && p.latest.SignatureData" class="sp-thumb" :src="p.latest.SignatureData" :alt="'Signature of ' + p.name" />
                                </div>
                            </div>
                            <button v-if="group.people.length > 4" type="button" class="rp-more" @click="expanded[group.id] = !expanded[group.id]">{{ expanded[group.id] ? 'Show less' : 'Show all ' + group.people.length }}</button>
                        </div>
                        <p v-if="!required.length && !unassigned.length" class="rp-empty">No standard users can view this yet, so no one is asked to sign it.</p>
                    </div>
                </template>
                <template v-else>
                    <div class="rz-ttl">
                        <h3>Sign-off</h3>
                        <div class="rz-sub">Version {{ version }}</div>
                    </div>
                    <!-- Only reachable through a share link (API playbook.ts shareToken): nothing to sign until it's published -->
                    <div v-if="node && node.NeedsApproval" class="rz-why">This isn't published, so it can't be signed until it is.</div>
                    <div v-else-if="myStatus == 'current'" class="rz-done">
                        <svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                        You signed v{{ version }} · {{ fmtDay(myLatest.Date) }}
                    </div>
                    <template v-else>
                        <button type="button" class="rz-sign" @click="openSign()">
                            <svg viewBox="0 0 24 24"><path d="M3 17c3-1 4-6 6-6s-1 6 1 6 3-3 4-3 1 3 3 3h4M3 21h18" /></svg>
                            Sign off on v{{ version }}
                        </button>
                        <div v-if="myStatus == 'stale'" class="rz-why">You signed on {{ fmtDay(myLatest.Date) }}. It's changed since.</div>
                    </template>
                </template>
            </section>

            <div class="rz-tabs" :style="'grid-template-columns:repeat(' + (isAdmin ? 3 : 2) + ',1fr)'">
                <button type="button" :class="open == 'files' ? 'rz-tab on' : 'rz-tab'" :aria-expanded="open == 'files'" @click="toggle('files')">
                    <svg viewBox="0 0 24 24"><path d="M15 7l-6.5 6.5a1.5 1.5 0 0 0 3 3L18 10a3 3 0 0 0-6-6l-6.5 6.5a4.5 4.5 0 0 0 9 9L21 13" /></svg>
                    <span class="rz-n">{{ files.length }}</span><span class="rz-l">Files</span>
                </button>
                <button type="button" :class="open == 'cmts' ? 'rz-tab on' : 'rz-tab'" :aria-expanded="open == 'cmts'" @click="toggle('cmts')">
                    <svg viewBox="0 0 24 24"><path d="M8 9h8M8 13h6M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-5l-5 3v-3H6a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h12z" /></svg>
                    <span class="rz-n">{{ comments.length }}</span><span class="rz-l">Comments</span>
                </button>
                <button v-if="isAdmin" type="button" :class="open == 'hist' ? 'rz-tab on' : 'rz-tab'" :aria-expanded="open == 'hist'" @click="toggle('hist')">
                    <svg viewBox="0 0 24 24"><path d="M12 8v4l2 2M3.05 11a9 9 0 1 1 .5 4M3 4v5h5" /></svg>
                    <span class="rz-n sm">{{ logs.length ? fmtDay(logs[0].Date) : '—' }}</span><span class="rz-l">History</span>
                </button>
            </div>

            <div v-if="open == 'files'" class="rz-panel">
                <div v-if="files.length" class="rp-scroll">
                    <div v-for="file in files" :key="file.ID" class="rp-file">
                        <svg viewBox="0 0 24 24"><path d="M14 3v4a1 1 0 0 0 1 1h4M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /></svg>
                        <div class="rp-pmain"><a class="rp-fn" :href="fileUrl(file)" target="_blank" rel="noopener">{{ file.Name }}</a></div>
                        <button v-if="isAdmin" type="button" class="rp-x" :aria-label="'Remove ' + file.Name" @click="fileToDelete = file.ID"><svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg></button>
                    </div>
                </div>
                <p v-if="!files.length && !isAdmin" class="rp-empty">No files yet.</p>
                <div v-if="isAdmin" class="rp-addfile">
                    <input type="text" v-model="fileName" placeholder="File name" aria-label="File name" />
                    <drop-file :key="dropKey" @fileChange="(f) => { file = f }" />
                    <button type="button" :class="busy ? 'btn disable-button' : 'btn'" @click="uploadFile()">Add file</button>
                </div>
            </div>
            <div v-else-if="open == 'cmts'" class="rz-panel">
                <div class="rp-scroll" ref="commentList">
                    <div v-for="(comment, i) in commentsOldestFirst" :key="i" class="rp-cmt">
                        <div class="rp-av">{{ initials(comment.User) }}</div>
                        <div class="rp-pmain">
                            <div class="rp-ch"><b>{{ comment.User }}</b><span>{{ ago(comment.Date) }}</span></div>
                            <div class="rp-ct" v-html="comment.CommentDisplay"></div>
                        </div>
                    </div>
                </div>
                <div class="rp-compose">
                    <textarea v-model="commentInput" ref="commentInput" :placeholder="isAdmin ? 'Add a comment. Type @ and an email to notify someone.' : 'Add a comment'" aria-label="Add a comment" @input="checkCommentInput()" @keydown.enter.exact.prevent="submitComment()"></textarea>
                    <div v-if="userAutoFill" class="rp-autofill" @click="setUserAutoFill()">{{ userAutoFill }}</div>
                    <div class="rp-cf"><span>Enter to post</span><button type="button" :class="busy ? 'btn disable-button' : 'btn'" @click="submitComment()">Post</button></div>
                </div>
            </div>
            <div v-else-if="open == 'hist' && isAdmin" class="rz-panel">
                <div class="rp-scroll">
                    <div v-for="(log, i) in (histAll ? historyRows : historyRows.slice(0, 6))" :key="i" :class="'rp-hrow ' + log.kind">
                        <i>
                            <svg v-if="log.kind == 'review'" viewBox="0 0 24 24"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>
                            <svg v-else-if="log.kind == 'approve'" viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                            <svg v-else viewBox="0 0 24 24"><path d="M4 20h4L18.5 9.5a2.83 2.83 0 0 0-4-4L4 16v4M13.5 6.5l4 4" /></svg>
                        </i>
                        <div class="rp-pmain">
                            <div class="rp-hw"><b>{{ log.User }}</b> {{ log.action }}<span v-if="log.count > 1"> ({{ log.count }}×)</span></div>
                            <div v-if="log.note" class="rp-hnote">“{{ log.note }}”</div>
                            <div class="rp-ps">{{ fmtWhen(log.Date) }}</div>
                        </div>
                    </div>
                </div>
                <p v-if="!historyRows.length" class="rp-empty">No history yet.</p>
                <button v-if="historyRows.length > 6" type="button" class="rp-more" @click="histAll = !histAll">{{ histAll ? 'Show less' : 'Show all ' + historyRows.length }}</button>
            </div>
            <button v-else-if="comments.length" type="button" class="rz-peek" @click="toggle('cmts')">
                <span class="rp-av">{{ initials(comments[0].User) }}</span>
                <span><b>{{ comments[0].User.split(' ')[0] }}</b> {{ stripHtml(comments[0].Comment) }}</span>
            </button>
        </div>
        <!-- Below the rail card, in its column (Playbook.vue puts Suggest an Improvement here); hidden with it -->
        <div v-if="$slots.default" class="rz-after"><slot /></div>

        <div v-if="showSign" class="error-msg-wrap">
            <div class="form-wrapper slide-up rz-sign-modal">
                <div class="form-close">
                    <button type="button" class="form-close-x" title="Close" @click="showSign = false">&times;</button>
                </div>
                <h3>Sign off on v{{ version }}</h3>
                <div v-if="signError" class="rz-err">{{ signError }}</div>
                <div class="rt-ack">By signing, you confirm you understand <b>{{ nodeName }}</b> (v{{ version }}), you'll follow it as part of supporting our team's success, and you'll tell management when it can be improved.</div>
                <label class="rz-attest"><input type="checkbox" v-model="signAttest" /><span>I've read this version of the process.</span></label>
                <div class="sp-top"><span class="rz-lbl">Sign here</span><a @click.prevent="($refs as any).signaturePad.clearSignature()">Clear</a></div>
                <div class="sp-box"><VueSignaturePad ref="signaturePad" /></div>
                <input type="text" v-model="signatureInput" placeholder="Type your name" aria-label="Your name" style="margin-top:15px" />
                <input :class="busy ? 'disable-button' : ''" type="submit" value="Sign" @click="submitSignature()" />
            </div>
        </div>

        <sign-off-cells v-if="signOffToShow" :signOff="signOffToShow" @close="signOffToShow = null" />
        <confirm-delete v-if="fileToDelete" @confirm="deleteFile()" @cancel="fileToDelete = null" msg="Are you sure you would like to delete this file?" />
    </aside>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import DropFile from '@/components/DropFile.vue';
import ConfirmDelete from '@/components/ConfirmDelete.vue';
import SignOffCells from './SignOffCells.vue';
import { readSeatSignOff, seatLabel } from '@/roleSheet';
import { publicUsersForNode } from '@/nodeViewers';

export default defineComponent({
    name: 'NodeAdminComponent',
    // node: the loaded tblNode row (Version, LastModified, LocationsJSON, DepartmentsJSON)
    props: [ 'slug', 'nodeName', 'node' ],
    emits: [ 'files-changed' ],
    components: {
        DropFile, ConfirmDelete, SignOffCells
    },
    data() {
        return {
            open: null as any,
            collapsed: false,
            logs: [] as any,
            signOffs: [] as any,
            comments: [] as any,
            files: [] as any,
            publicUsers: [] as any,
            users: [] as any,
            expanded: {} as any,
            histAll: false,
            busy: false,
            commentInput: '',
            userAutoFill: '',
            commentSelectionStart: 0,
            fileName: '',
            file: null as any,
            dropKey: 0,
            fileToDelete: null as any,
            showSign: false,
            signAttest: false,
            signatureInput: '',
            signError: '',
            signOffToShow: null as any
        }
    },
    computed: {
        isAdmin(): boolean {
            return this.$store.state.user.isAdmin;
        },
        version(): number {
            return this.node?.Version || 0;
        },
        // Sign-offs don't store a version, so a sign-off counts for the current version when it
        // happened after the last edit
        modifiedAt(): number {
            return this.node?.LastModified ? new Date(this.node.LastModified).getTime() : 0;
        },
        // One entry per signer (by phone), newest sign-off first
        signers(): any {
            const map: any = {};
            for(const s of this.signOffs) {
                const key = s.Phone || s.Signature;
                if(!map[key]) map[key] = { key, name: s.Signature, signOffs: [] };
                map[key].signOffs.push(s);
            }
            return map;
        },
        required(): any[] {
            if(!this.isAdmin || !this.node) return [];
            return publicUsersForNode(this.publicUsers, this.parse(this.node.LocationsJSON), this.parse(this.node.DepartmentsJSON))
                .map((u: any) => this.person(u.Phone, `${u.FirstName} ${u.LastName}`));
        },
        // Assigned people only: these drive the ring, status lines and avatar stack
        groups(): any {
            return {
                stale: this.required.filter((p: any) => p.latest && !this.isCurrent(p.latest)),
                none: this.required.filter((p: any) => !p.latest),
                cur: this.required.filter((p: any) => p.latest && this.isCurrent(p.latest))
            };
        },
        // Signers who can no longer view the node (moved dept/location, removed, phone changed)
        unassigned(): any[] {
            const requiredKeys = this.required.map((p: any) => p.key);
            return Object.keys(this.signers)
                .filter(key => !requiredKeys.includes(key))
                .map(key => this.person(key, this.signers[key].name));
        },
        signGroups(): any[] {
            return [
                { id: 'stale', title: 'Need to sign again', cls: 'warn', people: this.groups.stale },
                { id: 'none', title: 'Not signed', cls: 'bad', people: this.groups.none },
                { id: 'cur', title: 'Signed v' + this.version, cls: 'ok', people: this.groups.cur },
                { id: 'others', title: 'Also signed', cls: '', people: this.unassigned }
            ].filter(g => g.people.length);
        },
        // API returns newest first; the panel reads top-down into the compose box
        commentsOldestFirst(): any[] {
            return this.comments.slice().reverse();
        },
        pending(): any[] {
            return this.groups.stale.concat(this.groups.none);
        },
        signedCount(): number {
            return this.groups.cur.length;
        },
        ringDash(): string {
            const c = 2 * Math.PI * 22;
            const f = this.required.length ? this.signedCount / this.required.length : 0;
            return `${(c * f).toFixed(1)} ${c.toFixed(1)}`;
        },
        myLatest(): any {
            return this.signers[this.$store.state.user.naPhone]?.signOffs[0] || null;
        },
        myStatus(): string {
            if(!this.myLatest) return 'none';
            return this.isCurrent(this.myLatest) ? 'current' : 'stale';
        },
        // Audit log, newest first, with runs of the same action by the same person within an hour folded together
        historyRows(): any[] {
            const out: any[] = [];
            for(const log of this.logs) {
                const last = out[out.length - 1];
                if(last && last.User == log.User && last.Message == log.Message && new Date(last.start).getTime() - new Date(log.Date).getTime() <= 3600000) {
                    last.count++;
                    last.start = log.Date;
                } else {
                    const msg = `${log.Message}`.toLowerCase();
                    const kind = msg.includes('review') ? 'review' : msg.includes('approv') ? 'approve' : 'edit';
                    // Saves from the editor can carry a "What changed" note: "Updated: <note>" / "Small fix: <note>"
                    const noted = `${log.Message}`.match(/^(Updated|Small fix): ([\s\S]+)$/);
                    const action = noted ? noted[1].toLowerCase() == 'small fix' ? 'made a small fix' : 'updated' : msg == 'small fix' ? 'made a small fix' : msg;
                    out.push({ ...log, kind, action, note: noted ? noted[2] : '', count: 1, start: log.Date });
                }
            }
            return out;
        }
    },
    async created() {
        try {
            this.collapsed = localStorage.getItem('recordRailHidden') == '1';
        } catch(e) { /* storage unavailable; the rail starts open */ }
        if(this.isAdmin) {
            this.getLogs();
            this.getUsers();
            this.getPublicUsers();
        }
        this.getComments();
        this.getSignOffs();
        this.getFiles();
    },
    methods: {
        // Hides the rail down to its show button (or brings it back); remembered on this device for every page
        setCollapsed(collapsed: boolean) {
            this.collapsed = collapsed;
            try {
                localStorage.setItem('recordRailHidden', collapsed ? '1' : '0');
            } catch(e) { /* not remembered, that's fine */ }
        },
        toggle(section: string) {
            this.open = this.open == section ? null : section;
            if(this.open == 'cmts') {
                this.$nextTick(() => {
                    this.scrollCommentsToEnd();
                    (this.$refs.commentInput as any)?.focus();
                });
            }
        },
        // Newest comment sits at the bottom of the scroll box
        scrollCommentsToEnd() {
            const el = this.$refs.commentList as any;
            if(el) el.scrollTop = el.scrollHeight;
        },
        parse(json: any) {
            if(!json) return [];
            try { return JSON.parse(json) || []; } catch(e) { return []; }
        },
        person(key: string, name: string) {
            const signer = this.signers[key];
            return {
                key,
                name,
                latest: signer?.signOffs[0] || null,
                // Role sheet seat sign-offs, opened with SignOffCells
                cellSignOffs: (signer?.signOffs || []).filter((s: any) => s.Seat)
            };
        },
        isCurrent(signOff: any) {
            return new Date(signOff.Date).getTime() >= this.modifiedAt;
        },
        initials(name: string) {
            return `${name || ''}`.trim().split(/\s+/).map(w => w.charAt(0)).slice(0, 2).join('').toUpperCase() || '?';
        },
        fmtDay(date: any) {
            const d = new Date(date);
            const opts: any = { month: 'short', day: 'numeric' };
            if(d.getFullYear() != new Date().getFullYear()) opts.year = 'numeric';
            return d.toLocaleDateString(undefined, opts);
        },
        fmtWhen(date: any) {
            return this.fmtDay(date) + ', ' + new Date(date).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
        },
        ago(date: any) {
            const mins = Math.round((Date.now() - new Date(date).getTime()) / 60000);
            if(mins < 1) return 'just now';
            if(mins < 60) return mins + 'm ago';
            if(mins < 60 * 24) return Math.round(mins / 60) + 'h ago';
            if(mins < 60 * 48) return 'yesterday';
            return this.fmtDay(date);
        },
        stripHtml(html: string) {
            const div = document.createElement('div');
            div.innerHTML = `${html || ''}`.replace(/<br\s*\/?>/g, ' ');
            return div.textContent || '';
        },
        fileUrl(file: any) {
            return this.$store.state.apiUrl + this.$store.state.spacesUrl + file.SpacesPath;
        },
        async getLogs() {
            await axios.get(`${this.$store.state.apiUrl}/api/playbook/auditlog/${this.slug}`)
                .then(resp => { this.logs = resp.data; })
                .catch(e => { console.log(e); })
        },
        async getComments() {
            await axios.get(`${this.$store.state.apiUrl}/api/playbook/comments/${this.slug}`)
                .then(resp => {
                    for(const comment of resp.data) {
                        // tagged emails stand out, like the mockup's @mentions
                        comment.CommentDisplay = `${comment.Comment}`.replace(/\n/g, '<br/>').replace(/(^|\s|>)@([^\s<]+)/g, '$1<b class="rp-at">@$2</b>');
                    }
                    this.comments = resp.data;
                    this.$nextTick(() => this.scrollCommentsToEnd());
                })
                .catch(e => { console.log(e); })
        },
        async getUsers() {
            await axios.get(`${this.$store.state.apiUrl}/api/users`)
                .then(resp => {
                    this.users = resp.data.map((user: any) => `${user.Email}`.toLowerCase());
                })
                .catch(e => { console.log(e); })
        },
        async getPublicUsers() {
            await axios.get(`${this.$store.state.apiUrl}/api/publicusers`)
                .then(resp => { this.publicUsers = resp.data; })
                .catch(e => { console.log(e); })
        },
        async getSignOffs() {
            await axios.get(`${this.$store.state.apiUrl}/api/signoffs/${this.slug}`)
                .then(resp => {
                    for(const signOff of resp.data) {
                        signOff.DateDisplay = new Date(signOff.Date).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
                        signOff.Seat = readSeatSignOff(signOff);
                        signOff.SeatName = signOff.Seat ? seatLabel(signOff.Seat) || 'Seat' : '';
                    }
                    this.signOffs = resp.data;
                })
                .catch(e => { console.log(e); })
        },
        async getFiles() {
            await axios.get(`${this.$store.state.apiUrl}/api/playbook/files/${this.slug}`)
                .then(resp => { this.files = resp.data; })
                .catch(e => { console.log(e); })
        },
        async uploadFile() {
            if(this.busy) return;
            if(!this.fileName) {
                alert('Include a name');
                return;
            }
            if(!this.file) {
                alert('Include a file');
                return;
            }

            const formData = new FormData();
            formData.append('file', this.file);
            formData.set('name', this.fileName);
            formData.set('slug', this.slug);

            this.busy = true;
            await axios.post(`${this.$store.state.apiUrl}/api/playbook/nodefile`, formData)
                .then(() => {
                    this.file = null;
                    this.fileName = '';
                    this.dropKey++;
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error');
                })
            await this.getFiles();
            this.$emit('files-changed');
            this.busy = false;
        },
        async deleteFile() {
            await axios.delete(`${this.$store.state.apiUrl}/api/playbook/nodefile/${this.fileToDelete}`)
                .catch(e => {
                    console.log(e);
                    alert('There was an error');
                })
            this.fileToDelete = null;
            await this.getFiles();
            this.$emit('files-changed');
        },
        async submitComment() {
            if(!this.commentInput.trim() || this.busy) {
                return;
            }

            this.busy = true;

            const data = {
                slug: this.slug,
                comment: this.commentInput,
                nodeName: this.nodeName
            }

            await axios.post(`${this.$store.state.apiUrl}/api/playbook/comment`, data)
                .then(() => {
                    this.getComments();
                    this.commentInput = '';
                    this.userAutoFill = '';
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error');
                })

            this.busy = false;
        },
        // @-mention autofill: suggests a user email while typing after an @
        checkCommentInput() {
            if(!this.users.length) return;
            const el = this.$refs.commentInput as any;
            this.commentSelectionStart = el.selectionStart;
            for(let i = el.selectionStart - 1; i >= 0; i--) {
                const prevChar = el.value.charAt(i - 1);
                const char = el.value.charAt(i);
                if(char == ' ' || char == '\n') {
                    this.userAutoFill = '';
                    return;
                }
                if(char == '@' && (prevChar == ' ' || prevChar == '\n' || !prevChar)) {
                    const text = el.value.substring(i + 1, el.selectionStart).toLowerCase();
                    this.userAutoFill = (text && this.users.find((u: any) => u.includes(text))) || this.users[0];
                    return;
                }
            }
            this.userAutoFill = '';
        },
        setUserAutoFill() {
            const el = this.$refs.commentInput as any;
            let startIndex = 0;
            for(let i = this.commentSelectionStart - 1; i >= 0; i--) {
                const char = el.value.charAt(i);
                const prevChar = el.value.charAt(i - 1);
                if(char == '@' && (prevChar == ' ' || prevChar == '\n' || !prevChar)) {
                    startIndex = i;
                    break;
                }
            }

            this.commentInput = el.value.substring(0, startIndex + 1) + this.userAutoFill + el.value.substring(this.commentSelectionStart) + ' ';
            this.userAutoFill = '';
            el.focus();
        },
        openSign() {
            const user = this.$store.state.user;
            this.signatureInput = [ user.firstName, user.lastName ].filter(Boolean).join(' ');
            this.signAttest = false;
            this.signError = '';
            this.showSign = true;
        },
        async submitSignature() {
            if(this.busy) return;
            if(!this.signAttest) {
                this.signError = "Check the box to confirm you've read this version.";
                return;
            }
            const { isEmpty, data } = (this.$refs as any).signaturePad.saveSignature();
            if(isEmpty) {
                this.signError = 'Draw your signature in the box.';
                return;
            }
            if(!this.signatureInput.trim()) {
                this.signError = 'Type your name.';
                return;
            }

            this.busy = true;
            this.signError = '';

            const formData = {
                slug: this.slug,
                signature: this.signatureInput.trim(),
                nodeName: this.nodeName,
                signatureData: data
            }

            await axios.post(`${this.$store.state.apiUrl}/api/signoffs`, formData)
                .then(() => {
                    this.showSign = false;
                    this.getSignOffs();
                })
                .catch(e => {
                    console.log(e);
                    this.signError = 'There was an error. Try again.';
                })

            this.busy = false;
        },
        showSignOffCells(signOff: any) {
            this.signOffToShow = signOff;
        }
    }
});
</script>
